import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import apiService from '../services/apiService';

const DeviceList = () => {
    const { isAdmin } = useAuth();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadDevices();
    }, []);

    const loadDevices = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await apiService.getDevices();
            if (response.success) {
                setDevices(response.data);
            } else {
                setError(response.error || 'Failed to load devices');
            }
        } catch (err) {
            console.error('Load devices error:', err);
            setError('Failed to load devices');
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        try {
            setSyncing(true);
            const response = await apiService.syncDevicesFromCatalog();
            if (response.success) {
                await loadDevices();
            } else {
                alert('Sync failed: ' + (response.error || 'unknown'));
            }
        } catch (err) {
            console.error('Sync error:', err);
            alert('Sync failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading catalog SKUs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Device SKUs</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        One upload slot per firmware build (platformio env). Applicable catalog SKUs
                        sharing that build are listed as badges.
                    </p>
                </div>
                {isAdmin() && (
                    <button
                        onClick={handleSync}
                        disabled={syncing}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                    >
                        {syncing ? 'Syncing…' : 'Sync from catalog'}
                    </button>
                )}
            </div>

            <div className="bg-white shadow rounded-lg">
                {error ? (
                    <div className="p-6">
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-red-800">{error}</p>
                            <button
                                onClick={loadDevices}
                                className="mt-2 text-red-600 hover:text-red-800 underline"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                ) : devices.length === 0 ? (
                    <div className="p-6 text-center">
                        <p className="text-gray-500">No catalog SKUs found</p>
                        {isAdmin() && (
                            <button
                                onClick={handleSync}
                                className="mt-2 text-primary-600 hover:text-primary-800 underline"
                            >
                                Sync from catalog
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Build / representative
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Applicable SKUs
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        OTA target
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Firmware
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Latest
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {devices.map((device) => (
                                    <tr key={device.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {device.name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {device.sku_name || device.description || '—'}
                                            </div>
                                            {device.category && (
                                                <div className="text-xs text-gray-400">{device.category}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1.5">
                                                {(device.applicable_skus || []).length === 0 ? (
                                                    <span className="text-sm text-gray-400">—</span>
                                                ) : (
                                                    (device.applicable_skus || []).map((s) => (
                                                        <span
                                                            key={s.sku_code}
                                                            className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800"
                                                            title={s.sku_name || s.sku_code}
                                                        >
                                                            {s.sku_code}
                                                            {s.power_rating_kw != null
                                                                ? ` ${s.power_rating_kw} kW`
                                                                : ''}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-mono text-gray-900">
                                                {device.platformio_env || '—'}
                                            </div>
                                            {device.legacy_firmware_code && (
                                                <div className="text-xs text-gray-400">
                                                    {device.legacy_firmware_code}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {device.firmware_count || 0}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {device.latest_firmware?.version || '—'}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeviceList;
